/* eslint-disable import/no-cycle */
import { batch } from 'react-redux';
import { Logger } from '@saviynt/common';
import axios from 'axios';

import {
  CERTS_LIST_ALL_STATUS,
  CERTS_LIST_PREVIEW_STATUS,
  CERTS_LIST_STATUS,
  CERTS_LIST_TODO_TAB,
} from '../../../components/CertsList/constant';
import { httpService } from '../../../http-service/httpService';
import { LIMIT_CHARACTERS_FOR_QUERY } from '../../../page/CertificationList/CertsListBody/constant';
import { getRoleFilters, setGlobalError, setGlobalLoading } from '../action';
import {
  axiosTimeoutConfig,
  DATA_FETCH_ERROR,
  FORCED_CANCELLATION,
  FORCED_CANCELLATION_MESSAGE,
  SET_CERT_LIST_CAMPAIGN_INFO,
  SET_CERT_LIST_CURRENT_ACTION,
  SET_CERT_LIST_HAS_ADMIN_ROLE,
  SET_CERT_LIST_HAS_AUDITOR_LOGGED_IN,
  SET_CERT_LIST_IS_CONSULTEE_LOGIN,
  SET_CERT_LIST_IS_FIRST_LIST_LOAD_FINISHED,
  SET_CERT_LIST_IS_PAGE_LOAD_FINISHED,
  SET_CERT_LIST_IS_RELOAD_REQUIRED,
  SET_CERT_LIST_OFFSET,
  SET_CERT_LIST_ONE_TILE_ACTIONABLE,
  SET_CERT_LIST_RECENT_DATA,
  SET_CERT_LIST_TOTAL_ITEMS_COUNT,
  SET_CERT_LIST_TRANSITION_ID,
  SET_PREDEFINED_COMMENTS,
  VIEW_API_FAIL_MESSAGE,
} from '../types';
import {
  URL_LIST_DATA_API,
  URL_LIST_VIEW_API,
  URL_PREDEFINED_COMMENT_API,
} from '../urls';

let cancelTokenForGetListData;

export const setTotalItemsCount = (count) => (dispatch) => {
  dispatch({
    type: SET_CERT_LIST_TOTAL_ITEMS_COUNT,
    payload: count,
  });
};

export const setIsAtleastOneTileActionable = (actionable) => (dispatch) => {
  dispatch({
    type: SET_CERT_LIST_ONE_TILE_ACTIONABLE,
    payload: actionable,
  });
};

export const setFirstListLoadFinished = (isFinished) => (dispatch) => {
  dispatch({
    type: SET_CERT_LIST_IS_FIRST_LIST_LOAD_FINISHED,
    payload: isFinished,
  });

  if (!isFinished) {
    dispatch(setTotalItemsCount(0));
  }
};

export const setPageReload = (page) => (dispatch) => {
  dispatch({
    type: SET_CERT_LIST_IS_RELOAD_REQUIRED,
    payload: page,
  });
};

export const setTransitionTileId = (tileId) => (dispatch) => {
  dispatch({
    type: SET_CERT_LIST_TRANSITION_ID,
    payload: tileId,
  });
};

export const setCurrentAction = (action) => (dispatch) => {
  dispatch({
    type: SET_CERT_LIST_CURRENT_ACTION,
    payload: action,
  });
};

/**
 * Fetches and processes the view details for campaign list page.
 *
 * @function getViewDetails
 * @param {string} campaignKey - The unique identifier of the campaign.
 * @returns {Promise<boolean>} - A promise that resolves to true if the view details are successfully fetched, otherwise rejects with an error.
 *
 * @example
 * getViewDetails('campaign123')
 *   .then((success) => {
 *     if (success) {
 *       console.log('View details fetched successfully');
 *     }
 *   })
 *   .catch((error) => {
 *     console.error('Failed to fetch view details:', error);
 *   });
 */
export const getViewDetails = () => async (dispatch, getState) => {
  const { campaignKey } = getState().certifications.list.global;
  const body = {};

  if (campaignKey) {
    body.campaignKey = campaignKey;
  }

  dispatch(setGlobalLoading(true));
  dispatch(setGlobalError({ isError: false, error: null }));

  try {
    const { data } = await httpService.post(URL_LIST_VIEW_API, body);

    const {
      campaign,
      hasAdminrole,
      hasAuditorLoggedIn,
      isCampaignOwner,
      campaignType,
      autoRevoke,
      campaignOwnerName,
      isConsulteeLogin,
    } = data;

    const formatDate = (dateString) => {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();

      return `${day}/${month}/${year}`;
    };

    const startDate = campaign?.startDate
      ? formatDate(campaign.startDate)
      : null;
    const endDate = campaign?.endDate ? formatDate(campaign.endDate) : null;

    batch(() => {
      dispatch({
        type: SET_CERT_LIST_IS_PAGE_LOAD_FINISHED,
        payload: true,
      });

      dispatch({
        type: SET_CERT_LIST_HAS_ADMIN_ROLE,
        payload: Boolean(hasAdminrole || isCampaignOwner),
      });

      // Auditor logged in flag storing to redux
      if (hasAuditorLoggedIn) {
        dispatch({
          type: SET_CERT_LIST_HAS_AUDITOR_LOGGED_IN,
          payload: hasAuditorLoggedIn,
        });
      }

      dispatch({
        type: SET_CERT_LIST_IS_CONSULTEE_LOGIN,
        payload: isConsulteeLogin,
      });

      // This is for Cert List Header
      dispatch({
        type: SET_CERT_LIST_CAMPAIGN_INFO,
        payload: {
          campaignName: data?.campaign?.campaignName,
          campaignType,
          startDate,
          endDate,
          autoRevoke,
          campaignOwnerName,
          isCampaignOwner,
        },
      });
    });
    // to set the role filters
    dispatch(getRoleFilters());

    return true;
  } catch (error) {
    dispatch(setGlobalError({ isError: false, error: VIEW_API_FAIL_MESSAGE }));

    throw Error(error);
  }
};

/**
 * Fetches and processes certification list data for the certification list page.
 *
 * This function is a Redux thunk action that performs an API request to fetch certification data.
 * It manages the global loading state, handles errors, and updates the Redux store with the fetched data.
 * The function supports cancellation of in-flight API requests to avoid race conditions or redundant network calls.
 *
 * @function getListData
 * @async
 * @param {number} offset - The starting index for fetching data from the server.
 * @param {number} maxCount - The maximum number of items to fetch from the server.
 * @returns {Promise<void>} - A promise that resolves when the API call completes and the Redux store is updated.
 *
 * @description
 * - If a previous request is in progress, it cancels the ongoing request before initiating a new one.
 * - Dispatches actions to update the global loading state and reset any previous errors.
 * - On a successful API response:
 *   - Updates the total items count in the Redux store.
 *   - Stores the fetched results in the Redux store.
 *   - Flags the first load as completed.
 * - On a failure:
 *   - Dispatches an error state to the Redux store.
 *   - Ensures the loading state is reset.
 *
 * @example
 * // Dispatching the function from a component
 * dispatch(getListData(0, 20))
 *   .then(() => console.log('Data fetched successfully'))
 *   .catch((error) => console.error('Failed to fetch data:', error));
 */
export const getListData =
  ({ offset, max }) =>
  async (dispatch, getState) => {
    const {
      global: { campaignKey, isGlobalLoading },
      core: { isAdminLogin, hasAuditorLoggedIn },
      tabs: { activeTab },
      viewControls: {
        searchText,
        dynamicSortFilter: { sortByColumn, sortDirection: sortOrder },
        appliedRoleFilters,
      },
    } = getState().certifications.list;

    const body = { offset, max };

    dispatch({
      type: SET_CERT_LIST_OFFSET,
      payload: offset,
    });

    if (campaignKey) {
      body.campaignKey = campaignKey;
    }

    // when search text length is greater than 2
    if (searchText.length > LIMIT_CHARACTERS_FOR_QUERY) {
      body.sSearch = searchText;
    }

    // when sort is applied
    if (sortByColumn && sortOrder) {
      body.sort = sortByColumn;
      body.order = sortOrder;
    }

    // when role filter / Active and Inactive records filter
    if (
      (isAdminLogin || hasAuditorLoggedIn) &&
      appliedRoleFilters?.length > 0
    ) {
      body.certifierStatus = appliedRoleFilters
        .map((option) => option.value)
        .join(',');
    }

    if (activeTab && CERTS_LIST_STATUS[activeTab]) {
      if (
        (isAdminLogin || hasAuditorLoggedIn) &&
        activeTab === CERTS_LIST_TODO_TAB
      ) {
        body.status = `${CERTS_LIST_STATUS[activeTab]},${CERTS_LIST_PREVIEW_STATUS}`;
      } else {
        body.status = CERTS_LIST_STATUS[activeTab];
      }
    } else if (!isAdminLogin || !hasAuditorLoggedIn) {
      // when user is not admin passing all status to api
      body.status = CERTS_LIST_ALL_STATUS;
    }

    if (!isGlobalLoading) {
      dispatch(setGlobalLoading(true));
    }

    dispatch(setGlobalError({ isError: false, error: null }));

    try {
      if (cancelTokenForGetListData) {
        cancelTokenForGetListData.cancel(FORCED_CANCELLATION_MESSAGE);
      }

      cancelTokenForGetListData = axios.CancelToken.source();

      const { data } = await httpService.post(URL_LIST_DATA_API, body, {
        ...axiosTimeoutConfig,
        cancelToken: cancelTokenForGetListData.token,
      });

      const { response, totalCertifications, results } = data;

      if (response === 'Success') {
        dispatch(setTotalItemsCount(totalCertifications));
        dispatch({
          type: SET_CERT_LIST_RECENT_DATA,
          payload: results,
        });
        dispatch(setFirstListLoadFinished(true));
      }

      dispatch(setGlobalLoading(false));
    } catch (error) {
      batch(() => {
        dispatch({
          type: SET_CERT_LIST_RECENT_DATA,
          payload: null,
        });
        dispatch(
          setGlobalError({
            isError: error?.code !== FORCED_CANCELLATION,
            error:
              error?.code === FORCED_CANCELLATION
                ? FORCED_CANCELLATION_MESSAGE
                : DATA_FETCH_ERROR,
          })
        );
      });
    }
  };

export const getPredefinedComments = (action, certKey) => async (dispatch) => {
  const body = { certkey: certKey };

  try {
    const { data } = await httpService.post(
      URL_PREDEFINED_COMMENT_API,
      body,
      axiosTimeoutConfig
    );

    if (data?.response === 'Success') {
      dispatch({
        type: SET_PREDEFINED_COMMENTS,
        payload: {
          certKey,
          comments: data?.result,
        },
      });
    }

    return true;
  } catch (error) {
    Logger.log(error);

    throw Error(error);
  }
};
